import React, { useState, useEffect } from "react";
import "../styles.css";
import Spoiler from "./Spoiler";
import CoinFlipModal from "./CoinFlipModal";

const QuestionModal = ({ question, answeringTeamId, onClose, teams, onUseHelper, onUpdatePoints }) => {
  const [eliminatedOptions, setEliminatedOptions] = useState([]);
  const [fiftyFiftyUsed, setFiftyFiftyUsed] = useState(false);
  const [teacherAdviceUsed, setTeacherAdviceUsed] = useState(false);
  const [coinFlipUsed, setCoinFlipUsed] = useState(false);
  const [showCoinFlipModal, setShowCoinFlipModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answerConfirmed, setAnswerConfirmed] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState(false);
  
  const labels = ["a)", "b)", "c)", "d)"];
  
  // Reset state when the question changes
  useEffect(() => {
    setEliminatedOptions([]);
    setFiftyFiftyUsed(false);
    setTeacherAdviceUsed(false);
    setCoinFlipUsed(false);
    setShowCoinFlipModal(false);
    setSelectedOption(null);
    setAnswerConfirmed(false);
    setAnswerCorrect(false);
  }, [question]);
  
  if (!question) return null;
  
  // Get answering team info
  const answeringTeam = teams.find(team => team.id === answeringTeamId);
  
  // Find index of the correct answer in the options array
  const correctIndex = question.options.findIndex(opt => opt === question.answer);
  const correctLabel = correctIndex !== -1 ? labels[correctIndex] : "";
  
  // Handle 50-50 helper
  const handle5050 = () => {
    if (fiftyFiftyUsed || !question) return;
    
    // Find all wrong answers indices
    const wrongIndices = question.options
      .map((opt, idx) => (opt !== question.answer ? idx : -1))
      .filter(idx => idx !== -1);
    
    // Randomly select 2 wrong answers to eliminate
    const toEliminate = [];
    while (toEliminate.length < 2 && wrongIndices.length > 0) {
      const randomIndex = Math.floor(Math.random() * wrongIndices.length);
      toEliminate.push(wrongIndices[randomIndex]);
      wrongIndices.splice(randomIndex, 1);
    }
    
    setEliminatedOptions(toEliminate);
    setFiftyFiftyUsed(true);
    
    // Mark helper as used
    onUseHelper(answeringTeamId, 0);
  };
  
  // Handle teacher advice helper - just mark as used, no functionality
  const handleTeacherAdvice = () => {
    if (teacherAdviceUsed || !question) return;
    
    setTeacherAdviceUsed(true);
    
    // Mark helper as used
    onUseHelper(answeringTeamId, 1);
  };
  
  // Handle coin flip helper - open coin flip modal
  const handleCoinFlip = () => {
    if (coinFlipUsed || !question) return;
    
    setCoinFlipUsed(true);
    setShowCoinFlipModal(true);
    
    // Mark helper as used
    onUseHelper(answeringTeamId, 2);
  };
  
  // Check if answering team has specific helper available
  const hasHelper = (helperIndex) => {
    return answeringTeam && answeringTeam.helpers[helperIndex] === "1";
  };

  // Select an option 
  const handleOptionSelect = (optionIndex) => {
    if (answerConfirmed) return; // Prevent selection after confirmation
    setSelectedOption(optionIndex);
  };

  // Confirm answer and update points
  const confirmAnswer = () => {
    if (selectedOption === null || !answeringTeamId || answerConfirmed) return;
    
    const isCorrect = selectedOption === correctIndex;
    setAnswerConfirmed(true);
    setAnswerCorrect(isCorrect);
    
    // Update points based on correctness
    if (onUpdatePoints) {
      const pointsChange = isCorrect ? question.value : -question.value;
      onUpdatePoints(answeringTeamId, pointsChange);
    }
  };

  // Get option class based on state
  const getOptionClass = (idx) => {
    if (eliminatedOptions.includes(idx)) return 'eliminated';
    if (answerConfirmed) {
      if (idx === correctIndex) return 'correct-answer';
      if (idx === selectedOption && idx !== correctIndex) return 'wrong-answer';
    }
    return selectedOption === idx ? 'selected-option' : '';
  };

  return (
    <div className="modal">
      <div className="modal-content">
        {/* Answering Team Banner */}
        {answeringTeam && (
          <div className="answering-team-banner">
            <span className="answering-label">Odpovídá:</span>
            <span className="answering-team-name">{answeringTeam.name}</span>
          </div>
        )}

        <h2 className="modal-question">{question.question}</h2>

        {/* Helpers Section - Only show for answering team */}
        {answeringTeam && (
          <div className="helpers-section">
            {/* Teacher Advice Message when activated */}
            {teacherAdviceUsed && (
              <div className="teacher-advice-message" style={{ color: 'green', fontWeight: 'bold', margin: '10px 0' }}>
                Byla zaktivována rada učitele
              </div>
            )}
            
            {/* Only show helper buttons if no helper has been used yet */}
            {!fiftyFiftyUsed && !teacherAdviceUsed && !coinFlipUsed && (
              <>
                {/* 50-50 Button */}
{!fiftyFiftyUsed && !teacherAdviceUsed && !coinFlipUsed && hasHelper(0) && (
  <button
    className="btn helper-btn fifty-fifty-btn"
    onClick={handle5050}
  >
    50 na 50
  </button>
)}

{/* Teacher Advice Button */}
{!fiftyFiftyUsed && !teacherAdviceUsed && !coinFlipUsed && hasHelper(1) && (
  <button
    className="btn helper-btn fifty-fifty-btn"
    onClick={handleTeacherAdvice}
  >
    Rada učitele
  </button>
)}

{/* Coin Flip Button */}
{!fiftyFiftyUsed && !teacherAdviceUsed && !coinFlipUsed && hasHelper(2) && (
  <button
    className="btn helper-btn fifty-fifty-btn"
    onClick={handleCoinFlip}
  >
    Hod mincí
  </button>
)}
              </>
            )}
          </div>
        )}

        {/* Options List */}
        <ul className="options-list">
          {question.options.map((opt, idx) => (
            <li 
              key={idx} 
              className={`option-item ${getOptionClass(idx)}`}
              onClick={() => handleOptionSelect(idx)}
            >
              <strong>{labels[idx]}</strong> {opt}
            </li>
          ))}
        </ul>

        {/* Confirm Answer Button */}
        {selectedOption !== null && answeringTeamId && !answerConfirmed && (
          <button 
            className="btn confirm-answer-btn "
            onClick={confirmAnswer}
          >
            Potvrdit odpověď
          </button>
        )}
        {/* Answer (Hidden initially) */}
        {!answerConfirmed && (
          <p className="modal-answer">
            <Spoiler>{`${correctLabel} ${question.answer}`}</Spoiler>
          </p>
        )}
        {answerConfirmed && (
          <p className="modal-answer">
            <div className={`spoiler revealed`}>{`${correctLabel} ${question.answer}`}</div>
          </p>
        )}
        <button 
          className="modal-close" 
          onClick={onClose}
        >
          x
        </button>
      </div>
      
      {/* Coin Flip Modal */}
      <CoinFlipModal 
        isOpen={showCoinFlipModal} 
        onClose={() => setShowCoinFlipModal(false)} 
      />
    </div>
  );
};

export default QuestionModal;